"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useModal = exports.ModalContextProvider = void 0;
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const FocusTrap_1 = require("@mui/base/FocusTrap");
const modalContext = (0, react_1.createContext)(null);
const useDocumentOverflow = () => {
    const hide = () => {
        // override with important
        document.body.style.setProperty('overflow', 'hidden', 'important');
    };
    const show = () => {
        document.body.style.setProperty('overflow', 'auto');
    };
    return {
        hide,
        show,
    };
};
const useKeydown = (key, callback) => {
    (0, react_1.useEffect)(() => {
        const handler = (e) => {
            if (e.key === key) {
                callback();
            }
        };
        document.addEventListener('keydown', handler);
        return () => {
            document.removeEventListener('keydown', handler);
        };
    }, []);
};
const useModalContext = () => {
    const ctx = (0, react_1.useContext)(modalContext);
    if (!ctx) {
        throw new Error('No modal provider');
    }
    return ctx;
};
const useModalProvider = () => {
    const dialogRef = (0, react_1.useRef)(null);
    const [modal, setModal] = (0, react_1.useState)(null);
    const [isVisible, setIsVisible] = (0, react_1.useState)(false);
    const overflow = useDocumentOverflow();
    const show = (0, react_1.useCallback)((newModal) => {
        if (newModal) {
            setModal(newModal);
        }
        overflow.hide();
        setIsVisible(true);
    }, []);
    const hide = (0, react_1.useCallback)(() => {
        overflow.show();
        setIsVisible(false);
    }, []);
    useKeydown('Escape', () => {
        hide();
    });
    const ctx = (0, react_1.useMemo)(() => {
        return {
            setModal,
            show,
            hide,
            modal,
            setIsVisible,
        };
    }, [modal]);
    return {
        ctx,
        isVisible,
        dialogRef,
    };
};
// TODO: add focus trap
exports.ModalContextProvider = (0, react_1.memo)(({ children, }) => {
    const { ctx, isVisible, dialogRef } = useModalProvider();
    return (<modalContext.Provider value={ctx}>
			<div className={isVisible ? 'overflow-hidden' : 'overflow-auto'}>
				{children}
			</div>
			{isVisible && ctx.modal && (0, react_dom_1.createPortal)(<>
					<Overlay />
					<FocusTrap_1.FocusTrap open>
						<dialog className="flex flex-initial m-0 py-2 h-full overflow-auto bg-transparent fixed left-1/2 -translate-x-1/2 -translate-y-1/2 top-1/2 z-[100]" ref={dialogRef} open={isVisible}>
							{ctx.modal}
						</dialog>
					</FocusTrap_1.FocusTrap>
				</>, document.body)}
		</modalContext.Provider>);
});
const Overlay = (0, react_1.memo)(() => {
    const { hide } = useModalContext();
    return (<div onClick={hide} className="flex motion-safe:animate-scale-up backdrop-blur z-40 overflow-auto fixed top-0 left-0 w-full h-full h-lvh-full h-dvh-full h-svh-full items-center justify-center"/>);
});
function useModal(modal) {
    const { hide, show, setModal, } = useModalContext();
    (0, react_1.useEffect)(() => {
        if (modal) {
            setModal(modal);
        }
    }, []);
    return {
        hide,
        show,
    };
}
exports.useModal = useModal;
